import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import {
    Settings as SettingsIcon,
    Save, Building, Calendar,
    QrCode, Download,
    CreditCard, Network, Trash2, Ban,
    Bell, Shield, Gift,
    Info, Smartphone,
    ExternalLink, Mail,
    CheckCircle, XCircle,
    Loader, FlaskConical,
    HardDrive, ArrowUp, ArrowDown, Plus,
    MonitorSmartphone, UserRound, Upload
} from 'lucide-react'
import toast from 'react-hot-toast'
import { QRCodeCanvas } from 'qrcode.react'
import api from '../../services/api'

export default function SettingsPage() {
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()

    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [activeTab, setActiveTab] = useState('profile')

    // --- ACCUEIL RETOUR STRIPE ---
    useEffect(() => {
        const sessionId = searchParams.get('session_id')
        const isUpsell = searchParams.get('upsell') === 'success'
        if (sessionId) {
            if (isUpsell) {
                toast.success("Achat validé ! Votre espace de stockage a été augmenté. 📦", { duration: 6000 })
            } else {
                toast.success("Paiement validé ! Votre abonnement est actif. 🎉", { duration: 6000 })
            }
            // On nettoie l'URL pour ne pas réafficher le message au rafraîchissement
            navigate('/admin/settings', { replace: true })
            // On force l'onglet abonnement
            setActiveTab('storage')
        }
    }, [searchParams, navigate])

    const[storageInfo, setStorageInfo] = useState(null)
    const[landingConfig, setLandingConfig] = useState(null) // L'état du site vitrine


    // États pour le mot de passe
    const [pwdData, setPwdData] = useState({ current: '', new: '', confirm: '' })
    const [changingPwd, setChangingPwd] = useState(false)
    const [adminEmail, setAdminEmail] = useState('')
    const [subPlans, setSubPlans] = useState({ monthly: 19, annual: 192, quota: 20 }) // <-- Valeurs par défaut en cas de problème de récupération depuis Stripe
    const [workspaceId, setWorkspaceId] = useState(null) 

    // ---  Gestion de l'URL SaaS (Slug) ---
    const [workspaceSlug, setWorkspaceSlug] = useState('')
    const [originalSlug, setOriginalSlug] = useState('')
    const [slugStatus, setSlugStatus] = useState(null) // null | 'checking' | 'available' | 'taken' | 'invalid'

    // États des tests de connectivité
    const [smtpTestStatus, setSmtpTestStatus] = useState(null) // null | 'loading' | 'ok' | 'error'
    const [smtpTestMsg,    setSmtpTestMsg]    = useState('')
    const [smsTestStatus,  setSmsTestStatus]  = useState(null)
    const [smsTestMsg,     setSmsTestMsg]     = useState('')

    const [formTemplates, setFormTemplates] = useState([])
    const [publicFiles, setPublicFiles] = useState([])
    const [reasons, setReasons] = useState([])
    const [newReasonName, setNewReasonName] = useState('')

    const [settings, setSettings] = useState({
        admin_name: '',          // Onglet Profil
        admin_title: '',         // Onglet Profil
        cabinet_name: '',
        cabinet_email: '',
        cabinet_phone: '',
        cabinet_address: '',
        cabinet_city: '',        // Utilisé dans le PDF (ville)
        cabinet_rpps: '',
        cabinet_siret: '',
        site_url: '',
        avatar_url: '',
        admin_gender: 'F',
        legal_footer: '',
        show_phone_publicly: 'true',
        show_email_publicly: 'true',
        show_tariffs_publicly: 'true',

        appointment_new_duration: '60',
        appointment_followup_duration: '30',
        slot_interval: '15', // <-- paramètre pour le pas de recherche des créneaux
        price_new: '60',
        price_followup: '40',

        pre_consult_template_id: '',

        deposit_amount: '10',
        deposit_target: 'all', // <--  'all', 'new_patient', 'video'
        
        b2c_payment_gateway: 'stancer', // 'none', 'stancer', 'stripe'
        
        stancer_test_mode: 'true',
        stancer_public_key: '',
        stancer_secret_key: '',
        stancer_public_key_test: '',
        stancer_secret_key_test: '',
        
        stripe_b2c_test_mode: 'true',
        stripe_b2c_public_key: '',
        stripe_b2c_secret_key: '',
        stripe_b2c_public_key_test: '',
        stripe_b2c_secret_key_test: '',
        stripe_b2c_webhook_secret: '',
        stripe_b2c_webhook_secret_test: '',

        cancellation_delay_hours: '48',

        cancellation_fee_amount: '30', // <-- (Montant ou % de retenue)
        reminder_delay_hours: '48',
        reminder_24h_enabled: 'true',
        pre_consult_enabled: 'false',
        pre_consult_custom_message: '', 
        followup_consult_custom_message: '',
        patient_followup_enabled:   'true',
        patient_followup_days:      '30',
        same_day_booking_enabled: 'false',
        
        portal_show_weight: 'true',
        portal_show_meals: 'true',
        portal_show_activity: 'true',


        // Toggles On/Off canaux
        smtp_enabled: 'true',
        sms_enabled:  'true',

        sms_mode: 'cloud',
        sms_local_url: '',
        sms_local_user: '',
        sms_local_password: '',
        sms_cloud_url: 'https://api.sms-gate.app/3rdparty/v1/messages',
        sms_cloud_user: '',
        sms_cloud_password: '',

        smtp_host: '',
        smtp_port: '',
        smtp_user: '',
        smtp_password: '',
        smtp_from: ''
    })

    useEffect(() => { loadSettings() }, [])

    // --- Gestion du QR Code ---
    const [qrType, setQrType] = useState('url') // 'url' ou 'vcard'
    const [qrWithLogo, setQrWithLogo] = useState(false)

    // Calcul de l'URL publique actuelle
    const actualUrl = settings.site_url 
        ? (settings.site_url.startsWith('http') ? settings.site_url : `https://${settings.site_url}`)
        : `https://${workspaceSlug}.bellablue.contact`;

    // Construction de la VCard
    const vCardData = `BEGIN:VCARD
VERSION:3.0
FN:${settings.admin_name || ''}
TITLE:${settings.admin_title || ''}
ORG:${settings.cabinet_name || ''}
TEL;TYPE=WORK,VOICE:${settings.cabinet_phone || ''}
EMAIL;TYPE=PREF,INTERNET:${settings.cabinet_email || ''}
URL:${actualUrl}
ADR;TYPE=WORK:;;${(settings.cabinet_address || '').replace(/\n/g, ', ')};${settings.cabinet_city || ''};;;
END:VCARD`;

    const downloadQRCode = () => {
        const canvas = document.getElementById("qr-canvas");
        if (!canvas) return;
        const pngUrl = canvas.toDataURL("image/png").replace("image/png", "image/octet-stream");
        let downloadLink = document.createElement("a");
        downloadLink.href = pngUrl;
        downloadLink.download = `QR_${qrType === 'url' ? 'RDV' : 'Contact'}_${settings.cabinet_name || 'Cabinet'}.png`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        toast.success("QR Code téléchargé !");
    };
    // ----------------------------------------------

    const handleAvatarUpload = async (e) => {
        const file = e.target.files[0]
        if (!file) return
        if (file.size > 5 * 1024 * 1024) return toast.error("Image trop volumineuse (Max 5 Mo)")

        const formData = new FormData()
        formData.append('file', file)

        const toastId = toast.loading("Envoi de l'image...")
        try {
            const res = await api.post('/api/settings/avatar', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            })
            setSettings(prev => ({ ...prev, avatar_url: res.data.avatar_url }))
            toast.success("Photo de profil mise à jour !", { id: toastId })
        } catch (error) {
            toast.error(error.response?.data?.detail || "Erreur d'envoi", { id: toastId })
        }
    }


    const loadSettings = async () => {
        try {
            const[resSettings, resMe, resForms, resStorage, resReasons, resProfile, resPlans, resFiles] = await Promise.all([
                api.get('/api/settings/'),
                api.get('/api/auth/me'),
                api.get('/api/forms/templates'),
                api.get('/api/workspaces/storage'),
                api.get('/api/admin/reasons'),
                api.get('/api/workspaces/public-profile'),
                api.get('/api/workspaces/subscription-plans').catch(() => ({ data: { monthly: 49, annual: 490 } })),
                api.get('/api/library/files').catch(() => ({ data: [] }))
            ])
            setFormTemplates(resForms.data)
            setPublicFiles((resFiles?.data || []).filter(f => f.is_public)) // On ne garde que les fichiers avec cadenas vert !
            if (resPlans && resPlans.data) setSubPlans(resPlans.data)

            
            // Initialisation de l'ID et du Slug
            setWorkspaceId(resMe.data.workspace_id)
            setWorkspaceSlug(resProfile.data.slug)

            setOriginalSlug(resProfile.data.slug)

            setStorageInfo(resStorage.data)
            setReasons(resReasons.data)

            const settingsMap = {}
            resSettings.data.forEach(s => { settingsMap[s.setting_key] = s.setting_value })
            setSettings(prev => ({ ...prev, ...settingsMap }))
            setAdminEmail(resMe.data.email)

            // On parse le JSON de la landing page
            if (settingsMap.landing_page_config) {
                try {
                    setLandingConfig(JSON.parse(settingsMap.landing_page_config))
                } catch(e) {
                    setLandingConfig({})
                }
            } else {
                setLandingConfig({}) // <--- On crée un objet vide si rien n'existe !
            }
        } catch (e) {
            toast.error("Erreur de chargement des paramètres")
        } finally {
            setLoading(false)
        }
    }

    // --- Vérification du slug en temps réel (Debounce) ---
    useEffect(() => {
        if (!workspaceSlug) return;
        if (workspaceSlug === originalSlug) {
            setSlugStatus(null);
            return;
        }

        // Validation Regex (lettres, chiffres, tirets)
        if (!/^[a-z0-9-]+$/.test(workspaceSlug)) {
            setSlugStatus('invalid');
            return;
        }

        setSlugStatus('checking');
        const timer = setTimeout(() => {
            api.get(`/api/workspaces/check-slug?slug=${workspaceSlug}`)
               .then(res => setSlugStatus(res.data.available ? 'available' : 'taken'))
               .catch(() => setSlugStatus('error'))
        }, 500);

        return () => clearTimeout(timer);
    }, [workspaceSlug, originalSlug]) 

    const handleChange = (key, value) => {
        setSettings(prev => ({ ...prev, [key]: value }))
        // Reset du statut de test si on modifie les paramètres concernés
        if (key.startsWith('smtp_')) setSmtpTestStatus(null)
        if (key.startsWith('sms_'))  setSmsTestStatus(null)
    }

    const handleLandingChange = (section, key, value) => {
        setLandingConfig(prev => ({
            ...prev,
            [section]: {
                ...(prev[section] || {}),
                [key]: value
            }
        }))
    }

    const handlePasswordChange = async (e) => {
        e.preventDefault()
        if (pwdData.new !== pwdData.confirm) return toast.error("Les nouveaux mots de passe ne correspondent pas")
        if (pwdData.new.length < 6) return toast.error("Le mot de passe doit faire au moins 6 caractères")
        setChangingPwd(true)
        try {
            await api.post('/api/auth/change-password', {
                current_password: pwdData.current,
                new_password: pwdData.new
            })
            toast.success("Mot de passe modifié avec succès !")
            setPwdData({ current: '', new: '', confirm: '' })
        } catch (error) {
            toast.error(error.response?.data?.detail || "Erreur lors de la modification")
        } finally {
            setChangingPwd(false)
        }
    }

    const handleSave = async () => {
        setSaving(true)
        try {
            const promises = Object.entries(settings).map(([key, value]) =>
                api.patch(`/api/settings/${key}`, { value: String(value) }).catch(() => {})
            )
            
            // On ajoute la sauvegarde de notre JSON Vitrine
            if (landingConfig) {
                promises.push(
                    api.patch(`/api/settings/landing_page_config`, { value: JSON.stringify(landingConfig) })
                )
            }
            
            // Sauvegarde du slug s'il a changé et est valide
            if (workspaceSlug !== originalSlug && slugStatus === 'available') {
                promises.push(
                    api.patch('/api/workspaces/slug', { slug: workspaceSlug })
                       .then(() => setOriginalSlug(workspaceSlug))
                )
            }
            
            await Promise.all(promises)
            toast.success("Paramètres enregistrés")

        } catch (e) {

            toast.error("Erreur lors de la sauvegarde")
        } finally {
            setSaving(false)
        }
    }

    // --- Gestion des Motifs de Consultation ---
    const handleAddReason = async () => {
        if (!newReasonName.trim()) return;
        try {
            const res = await api.post('/api/admin/reasons', { name: newReasonName.trim(), sort_order: reasons.length });
            setReasons([...reasons, res.data]);
            setNewReasonName('');
            toast.success("Motif ajouté !");
        } catch (e) { toast.error("Erreur lors de l'ajout du motif"); }
    }

    const handleDeleteReason = async (id) => {
        if (!confirm("Supprimer ce motif ? (Les anciens RDV le conserveront dans leur historique)")) return;
        try {
            await api.delete(`/api/admin/reasons/${id}`);
            setReasons(reasons.filter(r => r.id !== id));
            toast.success("Motif supprimé !");
        } catch (e) { toast.error("Erreur lors de la suppression"); }
    }

    const handleMoveReason = async (index, direction) => {
        const newReasons = [...reasons];
        if (direction === 'up' && index > 0) {
            [newReasons[index - 1], newReasons[index]] = [newReasons[index], newReasons[index - 1]];
        } else if (direction === 'down' && index < newReasons.length - 1) {
            [newReasons[index + 1], newReasons[index]] =[newReasons[index], newReasons[index + 1]];
        } else return;
        
        setReasons(newReasons);
        
        // Sauvegarde silencieuse en arrière-plan
        try {
            await api.post('/api/admin/reasons/reorder', { reason_ids: newReasons.map(r => r.id) });
        } catch(e) { toast.error("Erreur lors du réordonnancement"); }
    }


    // --- Tests de connectivité ---
    const handleTestSmtp = async () => {
        // On sauvegarde d'abord les params SMTP pour que le test utilise les valeurs à l'écran
        const smtpKeys = ['smtp_host', 'smtp_port', 'smtp_user', 'smtp_password', 'smtp_from']
        await Promise.all(smtpKeys.map(key =>
            api.patch(`/api/settings/${key}`, { value: String(settings[key]) }).catch(() => {})
        ))
        setSmtpTestStatus('loading')
        setSmtpTestMsg('')
        try {
            const res = await api.post('/api/settings/test-smtp')
            setSmtpTestStatus('ok')
            setSmtpTestMsg(res.data.message)
        } catch (e) {
            setSmtpTestStatus('error')
            setSmtpTestMsg(e.response?.data?.detail || "Erreur inconnue")
        }
    }

    const handleTestSms = async () => {
        // On sauvegarde d'abord les params SMS pour que le test utilise les valeurs à l'écran
        const smsKeys = ['sms_mode', 'sms_local_url', 'sms_local_user', 'sms_local_password',
                         'sms_cloud_url', 'sms_cloud_user', 'sms_cloud_password']
        await Promise.all(smsKeys.map(key =>
            api.patch(`/api/settings/${key}`, { value: String(settings[key]) }).catch(() => {})
        ))
        setSmsTestStatus('loading')
        setSmsTestMsg('')
        try {
            const res = await api.post('/api/settings/test-sms')
            setSmsTestStatus('ok')
            setSmsTestMsg(res.data.message)
        } catch (e) {
            setSmsTestStatus('error')
            setSmsTestMsg(e.response?.data?.detail || "Erreur inconnue")
        }
    }

    // --- Sous-composants ---
    const Toggle = ({ checked, onChange }) => (
        <button
            type="button"
            onClick={() => onChange(!checked)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-sage-500 focus:ring-offset-2 ${
                checked ? 'bg-sage-600' : 'bg-gray-200'
            }`}
        >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                checked ? 'translate-x-6' : 'translate-x-1'
            }`} />
        </button>
    )

    const TestStatusBadge = ({ status, message }) => {
        if (!status) return null
        if (status === 'loading') return (
            <div className="flex items-center gap-2 text-sm text-gray-500 mt-3">
                <Loader className="w-4 h-4 animate-spin" /> Test en cours...
            </div>
        )
        if (status === 'ok') return (
            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2 mt-3">
                <CheckCircle className="w-4 h-4 flex-shrink-0" /> {message}
            </div>
        )
        return (
            <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mt-3">
                <XCircle className="w-4 h-4 flex-shrink-0" /> {message}
            </div>
        )
    }

    if (loading) return <div className="p-12 text-center"><div className="spinner mx-auto"/></div>

    // Calcul de l'alarme pour l'onglet
    const trialDaysLeft = storageInfo?.created_at 
        ? Math.max(0, 14 - Math.floor((new Date() - new Date(storageInfo.created_at)) / (1000 * 60 * 60 * 24)))
        : 14;
    const showTrialWarning = storageInfo?.subscription_status === 'trialing' && trialDaysLeft <= 5;

    const TABS = [
        { id: 'profile',       label: 'Profil',  icon: Building  },
        { id: 'booking',       label: 'Consultations',   icon: Calendar  },
        { id: 'payment',       label: 'Règlement',       icon: CreditCard },
        { id: 'notifications', label: 'Notifications',   icon: Bell      },
        { id: 'website',       label: 'Site & Portail',  icon: MonitorSmartphone },
        { id: 'storage',       label: 'Abonnement',      icon: HardDrive, warning: showTrialWarning },
        { id: 'security',      label: 'Sécurité',        icon: Shield    },
    ]

    return (
        <div className="max-w-4xl mx-auto flex flex-col h-[calc(100vh-100px)]">
            <div className="mb-6">
                <div className="flex gap-6 border-b border-gray-200 overflow-x-auto scrollbar-hide">
                    {TABS.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`pb-3 px-1 flex items-center gap-2 text-sm font-medium transition-all relative whitespace-nowrap ${
                                activeTab === tab.id ? 'text-sage-700' : 'text-gray-400 hover:text-gray-600'
                            }`}
                        >
                            <tab.icon className="w-4 h-4" />
                            {tab.label}
                            {tab.warning && (
                                <span className="flex h-2 w-2 relative -ml-1 -mt-3">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                                </span>
                            )}
                            {activeTab === tab.id && <span className="absolute bottom-0 left-0 w-full h-0.5 bg-sage-500 rounded-t-full" />}
                        </button>
                    ))}
                </div>
            </div>

            <div className="flex-1 bg-white rounded-2xl shadow-sm border border-gray-200 flex flex-col overflow-hidden">
                <div className="flex-1 overflow-y-auto p-6 md:p-8">

                    {/* ONGLET : PROFIL CABINET */}
                    {activeTab === 'profile' && (
                        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="flex gap-2">
                                    <div className="w-24 shrink-0">
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Genre</label>
                                        <select className="w-full text-sm py-2 bg-gray-50 border-gray-200" value={settings.admin_gender || 'F'} onChange={e => handleChange('admin_gender', e.target.value)}>
                                            <option value="F">Mme</option>
                                            <option value="M">M.</option>
                                        </select>
                                    </div>
                                    <div className="flex-1">
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Votre Nom & Prénom</label>
                                        <input className="w-full font-medium text-sm py-2" value={settings.admin_name || ''} onChange={e => handleChange('admin_name', e.target.value)} placeholder="Ex: Dr. Dupont" />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Votre Titre (Spécialité)</label>
                                    <input className="w-full" value={settings.admin_title || ''} onChange={e => handleChange('admin_title', e.target.value)} placeholder="Ex: Diététicienne" />
                                </div>
                                
                                {/* L'APERÇU EN DIRECT */}
                                <div className="md:col-span-2 bg-sage-50 p-3 rounded-lg border border-sage-100 mt-[-10px]">
                                    <p className="text-[10px] font-bold text-sage-600 uppercase mb-1">Aperçu sur vos documents et factures :</p>
                                    <p className="text-sm text-sage-800 italic">
                                        "Je soussigné{settings.admin_gender === 'F' ? 'e' : ''} {settings.admin_name || '[Votre Nom]'}, exerçant en tant que {settings.admin_title || '[Votre Titre]'}, déclare avoir reçu..."
                                    </p>
                                </div>

                                <div className="md:col-span-2 pt-4 border-t border-gray-100">
                                    <h4 className="text-sm font-bold text-gray-700 mb-3">Informations du Cabinet</h4>
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Nom du Cabinet</label>
                                    <input className="w-full font-medium" value={settings.cabinet_name} onChange={e => handleChange('cabinet_name', e.target.value)} placeholder="Ex: Cabinet Doc City" />
                                </div>

                                {/* L'URL SAAS BELLA BLUE */}
                                <div className="md:col-span-2 bg-sage-50 border border-sage-200 p-4 rounded-xl">
                                    <label className="block text-xs font-bold text-sage-800 uppercase mb-1">Adresse de votre site BellaBlue</label>
                                    <p className="text-xs text-sage-600 mb-3">Vos patients pourront prendre RDV directement sur ce lien. Vous pouvez le personnaliser.</p>
                                    
                                    <div className="flex items-center">
                                        <span className="text-sm font-mono text-gray-500 bg-white border border-r-0 border-gray-300 rounded-l-lg py-2 pl-3 pr-1">https://</span>
                                        <input 
                                            className={`w-full font-mono text-sm border-gray-300 py-2 px-2 focus:ring-sage-500 ${
                                                slugStatus === 'taken' || slugStatus === 'invalid' ? 'text-red-600 bg-red-50 focus:ring-red-500 border-red-300' 
                                                : slugStatus === 'available' ? 'text-green-700 bg-green-50 focus:ring-green-500 border-green-300' 
                                                : 'text-sage-800'
                                            }`} 
                                            value={workspaceSlug} 
                                            onChange={e => setWorkspaceSlug(e.target.value.toLowerCase())} 
                                            placeholder="mon-cabinet" 
                                        />
                                        <span className="text-sm font-mono text-gray-500 bg-white border border-l-0 border-gray-300 rounded-r-lg py-2 pr-3 pl-1">.bellablue.contact</span>
                                    </div>

                                    {/* Feedback temps réel */}
                                    <div className="mt-2 h-4">
                                        {slugStatus === 'checking' && <span className="text-xs text-sage-600 flex items-center gap-1"><div className="spinner w-3 h-3 border-sage-600" /> Vérification...</span>}
                                        {slugStatus === 'available' && <span className="text-xs text-green-600 font-bold flex items-center gap-1">✓ Cette URL est disponible !</span>}
                                        {slugStatus === 'taken' && <span className="text-xs text-red-600 font-bold flex items-center gap-1">✗ Cette URL est déjà utilisée.</span>}
                                        {slugStatus === 'invalid' && <span className="text-xs text-red-600 font-bold flex items-center gap-1">✗ Uniquement des lettres minuscules, chiffres et tirets.</span>}
                                    </div>
                                </div>

                                {/* LE CUSTOM DOMAIN */}
                                <div className="md:col-span-2">
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Domaine Personnalisé (Optionnel)</label>
                                    <input 
                                        className="w-full font-mono text-sm text-blue-600 bg-blue-50 border-blue-200 focus:bg-white" 
                                        value={settings.site_url || ''} 
                                        onChange={e => handleChange('site_url', e.target.value)} 
                                        placeholder="ex: https://www.votreDomaine.fr" 
                                    />
                                    <p className="text-[10px] text-gray-400 mt-1">Laissez vide pour utiliser le sous-domaine par défaut.</p>
                                </div>
                                
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Email de contact</label>
                                    <input type="email" className="w-full" value={settings.cabinet_email} onChange={e => handleChange('cabinet_email', e.target.value)} />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Téléphone</label>
                                    <input className="w-full" value={settings.cabinet_phone} onChange={e => handleChange('cabinet_phone', e.target.value)} />
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Adresse complète</label>
                                    <textarea className="w-full" rows="2" value={settings.cabinet_address || ''} onChange={e => handleChange('cabinet_address', e.target.value)} placeholder="Ex: 10 rue de la Paix" />
                                </div>
                                
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Ville (Pour l'édition des PDF)</label>
                                    <input className="w-full" value={settings.cabinet_city || ''} onChange={e => handleChange('cabinet_city', e.target.value)} placeholder="Ex: Paris" />
                                </div>
                                
                                <div className="md:col-span-2 pt-4 border-t border-gray-100">
                                    <h4 className="text-sm font-bold text-gray-700 mb-3">Légal</h4>
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">N° RPPS / ADELI</label>
                                    <input className="w-full font-mono text-sm" value={settings.cabinet_rpps} onChange={e => handleChange('cabinet_rpps', e.target.value)} />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">N° SIRET</label>
                                    <input className="w-full font-mono text-sm" value={settings.cabinet_siret || ''} onChange={e => handleChange('cabinet_siret', e.target.value)} />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Pied de page légal (PDF)</label>
                                    <input className="w-full text-sm" value={settings.legal_footer || ''} onChange={e => handleChange('legal_footer', e.target.value)} placeholder="Ex: TVA non applicable, art. 293 B du CGI" />
                                    <p className="text-[10px] text-gray-400 mt-1">S'affichera en bas de toutes vos factures et courriers.</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ONGLET : CONSULTATIONS */}
                    {activeTab === 'booking' && (
                        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4">
                            <div>
                                <h3 className="font-bold text-gray-700 border-b border-gray-100 pb-2 mb-4">Première consultation (Nouveau)</h3>
                                <div className="grid grid-cols-2 gap-6 max-w-lg">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Durée (minutes)</label>
                                        <input type="number" className="w-full" value={settings.appointment_new_duration} onChange={e => handleChange('appointment_new_duration', e.target.value)} />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Tarif par défaut (€)</label>
                                        <input type="number" className="w-full" value={settings.price_new} onChange={e => handleChange('price_new', e.target.value)} />
                                    </div>
                                </div>
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-700 border-b border-gray-100 pb-2 mb-4">Consultation de Suivi</h3>
                                <div className="grid grid-cols-2 gap-6 max-w-lg">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Durée (minutes)</label>
                                        <input type="number" className="w-full" value={settings.appointment_followup_duration} onChange={e => handleChange('appointment_followup_duration', e.target.value)} />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Tarif par défaut (€)</label>
                                        <input type="number" className="w-full" value={settings.price_followup} onChange={e => handleChange('price_followup', e.target.value)} />
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white p-4 rounded-xl border border-gray-200 mt-6 shadow-sm flex flex-col sm:flex-row justify-between sm:items-center gap-4">
                                <div>
                                    <p className="font-bold text-gray-800">Intervalle de recherche (Agenda)</p>
                                    <p className="text-xs text-gray-500 mt-0.5">Définit l'espacement entre deux créneaux proposés aux patients.</p>
                                </div>
                                <select 
                                    value={settings.slot_interval} 
                                    onChange={e => handleChange('slot_interval', e.target.value)}
                                    className="w-full sm:w-48 text-sm bg-gray-50 border-gray-200 rounded-lg focus:ring-sage-500"
                                >
                                    <option value="10">Toutes les 10 minutes</option>
                                    <option value="15">Toutes les 15 minutes</option>
                                    <option value="20">Toutes les 20 minutes</option>
                                    <option value="30">Toutes les 30 minutes</option>
                                </select>
                            </div>

                            <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-xl border border-emerald-100 mt-4">
                                <div>
                                    <p className="font-bold text-emerald-900">Rendez-vous le jour-même</p>
                                    <p className="text-xs text-emerald-700 mt-0.5">Autoriser les patients à réserver un créneau pour aujourd'hui (les heures passées seront automatiquement masquées).</p>
                                </div>
                                <Toggle
                                    checked={settings.same_day_booking_enabled === 'true'}
                                    onChange={(val) => handleChange('same_day_booking_enabled', val ? 'true' : 'false')}
                                />
                            </div>


                            <div className="pt-6 border-t border-gray-100">
                                <h3 className="font-bold text-gray-700 pb-2 mb-4 flex items-center gap-2">🤖 Secrétariat Virtuel</h3>
                                <div className="flex items-center justify-between p-4 bg-purple-50 rounded-xl border border-purple-100 mb-4">
                                    <div>
                                        <p className="font-bold text-purple-900">Liste d'attente publique</p>
                                        <p className="text-xs text-purple-700 mt-0.5">Permettre aux patients de faire une demande exceptionnelle s'ils ne trouvent pas d'horaire (Crée un Pense-Bête).</p>
                                    </div>
                                    <Toggle
                                        checked={settings.waitlist_enabled !== 'false'}
                                        onChange={(val) => handleChange('waitlist_enabled', val ? 'true' : 'false')}
                                    />
                                </div>
                                <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-xl border border-yellow-100 mb-4">
                                    <div className="flex-1">
                                        <p className="font-bold text-yellow-900">Envois de préparation (Nouveau Patient)</p>
                                        <p className="text-xs text-yellow-700 mt-0.5">Envoyer automatiquement un questionnaire et/ou un document PDF aux nouveaux patients avec leur email de confirmation.</p>
                                        {settings.pre_consult_enabled === 'true' && (
                                            <div className="mt-4 p-3 bg-white/50 border border-yellow-200 rounded-lg animate-in zoom-in-95">
                                                <p className="text-[10px] font-bold text-yellow-800 uppercase mb-1">Questionnaire à remplir</p>
                                                <select 
                                                    value={settings.pre_consult_template_id || '0'} 
                                                    onChange={e => handleChange('pre_consult_template_id', e.target.value)}
                                                    className="mb-3 block text-xs bg-white border border-yellow-200 rounded py-2 px-2 w-full max-w-sm text-yellow-900 font-medium"
                                                >
                                                    <option value="-1">❌ Aucun questionnaire</option>
                                                    <option value="0">📝 Formulaire standard (Par défaut)</option>
                                                    {formTemplates.filter(t => !t.is_internal).map(t => <option key={t.id} value={t.id}>📝 {t.name}</option>)}
                                                </select>
                                                
                                                {/* NOUVEAU: Choix du fichier joint */}
                                                <p className="text-[10px] font-bold text-yellow-800 uppercase mb-1 border-t border-yellow-200 pt-3">Document joint (Guide, E-Book...)</p>
                                                <select 
                                                    value={settings.pre_consult_file_id || ''} 
                                                    onChange={e => handleChange('pre_consult_file_id', e.target.value)}
                                                    className="mb-1 block text-xs bg-white border border-yellow-200 rounded py-2 px-2 w-full max-w-sm text-yellow-900 font-medium"
                                                >
                                                    <option value="">❌ Aucun document</option>
                                                    {publicFiles.map(f => <option key={f.id} value={f.id}>📎 {f.name}</option>)}
                                                </select>
                                                <p className="text-[10px] text-yellow-700 italic">Seuls les fichiers avec un "Cadenas Vert" dans votre bibliothèque apparaissent ici.</p>
                                                
                                                {/* NOUVEAU : Message personnalisé */}
                                                <p className="text-[10px] font-bold text-yellow-800 uppercase mb-1 border-t border-yellow-200 pt-3">Mot d'accompagnement (Optionnel)</p>
                                                <textarea 
                                                    rows="2"
                                                    value={settings.pre_consult_custom_message || ''}
                                                    onChange={e => handleChange('pre_consult_custom_message', e.target.value)}
                                                    placeholder="Ex: Merci de remplir le carnet alimentaire sur 3 jours..."
                                                    className="w-full text-xs bg-white border border-yellow-200 rounded p-2 text-yellow-900 focus:ring-yellow-400 resize-none"
                                                />
                                            </div>
                                        )}
                                    </div>

                                    <Toggle
                                        checked={settings.pre_consult_enabled === 'true'}
                                        onChange={(val) => handleChange('pre_consult_enabled', val ? 'true' : 'false')}
                                    />
                                </div>
                                
                                {/* NOUVEAU : ENVOIS DE PREPARATION POUR LES SUIVIS */}
                                <div className="flex items-center justify-between p-4 bg-green-50 rounded-xl border border-green-100 mb-4">
                                    <div className="flex-1">
                                        <p className="font-bold text-green-900">Envois de préparation (Consultation de Suivi)</p>
                                        <p className="text-xs text-green-700 mt-0.5">Envoyer automatiquement un questionnaire et/ou un document PDF aux patients réguliers avec leur email de confirmation.</p>
                                        {settings.followup_consult_enabled === 'true' && (
                                            <div className="mt-4 p-3 bg-white/50 border border-green-200 rounded-lg animate-in zoom-in-95">
                                                <p className="text-[10px] font-bold text-green-800 uppercase mb-1">Questionnaire à remplir</p>
                                                <select 
                                                    value={settings.followup_consult_template_id || '-1'} 
                                                    onChange={e => handleChange('followup_consult_template_id', e.target.value)}
                                                    className="mb-3 block text-xs bg-white border border-green-200 rounded py-2 px-2 w-full max-w-sm text-green-900 font-medium"
                                                >
                                                    <option value="-1">❌ Aucun questionnaire</option>
                                                    {formTemplates.filter(t => !t.is_internal).map(t => <option key={t.id} value={t.id}>📝 {t.name}</option>)}
                                                </select>
                                                
                                                <p className="text-[10px] font-bold text-green-800 uppercase mb-1 border-t border-green-200 pt-3">Document joint (Carnet vierge, Guide...)</p>
                                                <select 
                                                    value={settings.followup_consult_file_id || ''} 
                                                    onChange={e => handleChange('followup_consult_file_id', e.target.value)}
                                                    className="mb-1 block text-xs bg-white border border-green-200 rounded py-2 px-2 w-full max-w-sm text-green-900 font-medium"
                                                >
                                                    <option value="">❌ Aucun document</option>
                                                    {publicFiles.map(f => <option key={f.id} value={f.id}>📎 {f.name}</option>)}
                                                </select>
                                                <p className="text-[10px] text-green-700 italic">Seuls les fichiers avec un "Cadenas Vert" dans votre bibliothèque apparaissent ici.</p>
                                                 
                                                {/* NOUVEAU : Message personnalisé */}
                                                <p className="text-[10px] font-bold text-green-800 uppercase mb-1 border-t border-green-200 pt-3">Mot d'accompagnement (Optionnel)</p>
                                                <textarea 
                                                    rows="2"
                                                    value={settings.followup_consult_custom_message || ''}
                                                    onChange={e => handleChange('followup_consult_custom_message', e.target.value)}
                                                    placeholder="Ex: Merci de remplir le carnet alimentaire sur 3 jours..."
                                                    className="w-full text-xs bg-white border border-green-200 rounded p-2 text-green-900 focus:ring-green-400 resize-none"
                                                />


                                            </div>
                                        )}
                                    </div>
                                    <Toggle
                                        checked={settings.followup_consult_enabled === 'true'}
                                        onChange={(val) => handleChange('followup_consult_enabled', val ? 'true' : 'false')}
                                    />
                                </div>

                                {/* Relance patients inactifs */}
                                <div className={`p-4 rounded-xl border transition-all ${
                                    settings.patient_followup_enabled === 'true'
                                        ? 'bg-blue-50 border-blue-100'
                                        : 'bg-gray-50 border-gray-100'
                                }`}>
                                    <div className="flex items-center justify-between mb-3">
                                        <div>
                                            <p className="font-bold text-blue-900">Relance patients inactifs</p>
                                            <p className="text-xs text-blue-700 mt-0.5">
                                                Crée une tâche dans le Pense-Bête pour chaque patient sans RDV depuis X jours
                                            </p>
                                        </div>
                                        <Toggle
                                            checked={settings.patient_followup_enabled === 'true'}
                                            onChange={(val) => handleChange('patient_followup_enabled', val ? 'true' : 'false')}
                                        />
                                    </div>
                                    {settings.patient_followup_enabled === 'true' && (
                                        <div className="flex items-center gap-3 mt-2">
                                            <label className="text-xs font-bold text-gray-500 uppercase whitespace-nowrap">
                                                Délai sans RDV
                                            </label>
                                            <div className="relative w-24">
                                                <input
                                                    type="number"
                                                    min="7" max="365"
                                                    className="w-full text-sm font-bold text-blue-700 pr-10 text-right"
                                                    value={settings.patient_followup_days}
                                                    onChange={e => handleChange('patient_followup_days', e.target.value)}
                                                />
                                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">j</span>
                                            </div>
                                            <p className="text-xs text-gray-400 italic">
                                                Vérification quotidienne à minuit
                                            </p>
                                        </div>
                                    )}
                                </div>
                                
                            </div> {/* fin section secretariat */}

                            {/* SECTION MOTIFS DE CONSULTATION */}
                            <div className="pt-6 border-t border-gray-100">
                                <h3 className="font-bold text-gray-700 pb-2 mb-4 flex items-center gap-2">📋 Motifs de consultation</h3>
                                <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                                    <div className="flex gap-2 mb-4">
                                        <input 
                                            type="text" 
                                            placeholder="Nouveau motif (ex: Bilan Micronutrition)..." 
                                            value={newReasonName}
                                            onChange={e => setNewReasonName(e.target.value)}
                                            onKeyDown={e => e.key === 'Enter' && handleAddReason()}
                                            className="flex-1 text-sm bg-white"
                                        />
                                        <button onClick={handleAddReason} className="btn btn-primary px-4 py-2 flex items-center gap-2 text-sm shadow-sm">
                                            <Plus className="w-4 h-4" /> Ajouter
                                        </button>
                                    </div>
                                    <div className="space-y-2 max-h-64 overflow-y-auto pr-2 scrollbar-thin">
                                        {reasons.length === 0 && <p className="text-sm text-gray-400 italic text-center py-4">Aucun motif configuré.</p>}
                                        {reasons.map((reason, idx) => (
                                            <div key={reason.id} className="flex justify-between items-center bg-white border border-gray-200 px-3 py-2 rounded-lg group shadow-sm">
                                                <span className="text-sm font-medium text-gray-700">{reason.name}</span>
                                                <div className="flex items-center gap-1 opacity-50 group-hover:opacity-100 transition-opacity">
                                                    <button onClick={() => handleMoveReason(idx, 'up')} disabled={idx === 0} className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"><ArrowUp className="w-4 h-4 text-gray-500"/></button>
                                                    <button onClick={() => handleMoveReason(idx, 'down')} disabled={idx === reasons.length - 1} className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"><ArrowDown className="w-4 h-4 text-gray-500"/></button>
                                                    <div className="w-px h-4 bg-gray-200 mx-1"></div>
                                                    <button onClick={() => handleDeleteReason(reason.id)} className="p-1 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4 text-red-500"/></button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ONGLET : PAIEMENT */}
                    {activeTab === 'payment' && (
                        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4">
                            
                            {/* CHOIX DE LA PASSERELLE B2C */}
                            <section>
                                <h3 className="font-bold text-gray-700 border-b border-gray-100 pb-2 mb-4">Passerelle de paiement (Encaisser vos patients)</h3>
                                
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                                    <button 
                                        onClick={() => handleChange('b2c_payment_gateway', 'none')}
                                        className={`p-4 rounded-xl border-2 text-left transition-all ${settings.b2c_payment_gateway === 'none' ? 'border-gray-500 bg-gray-50 ring-1 ring-gray-500' : 'border-gray-100 hover:border-gray-300 bg-white'}`}
                                    >
                                        <Ban className={`w-6 h-6 mb-2 ${settings.b2c_payment_gateway === 'none' ? 'text-gray-700' : 'text-gray-400'}`} />
                                        <p className="font-bold text-gray-800">Aucun</p>
                                        <p className="text-[10px] text-gray-500 mt-1">Paiement au cabinet uniquement. Désactive les liens de paiement.</p>
                                    </button>

                                    <button 
                                        onClick={() => handleChange('b2c_payment_gateway', 'stancer')}
                                        className={`p-4 rounded-xl border-2 text-left transition-all ${settings.b2c_payment_gateway === 'stancer' ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-gray-100 hover:border-gray-300 bg-white'}`}
                                    >
                                        <CreditCard className={`w-6 h-6 mb-2 ${settings.b2c_payment_gateway === 'stancer' ? 'text-blue-600' : 'text-gray-400'}`} />
                                        <p className="font-bold text-gray-800">Stancer</p>
                                        <p className="text-[10px] text-gray-500 mt-1">Idéal TPE/PME en France. Taux très bas et sans abonnement.</p>
                                    </button>

                                    <button 
                                        onClick={() => handleChange('b2c_payment_gateway', 'stripe')}
                                        className={`p-4 rounded-xl border-2 text-left transition-all ${settings.b2c_payment_gateway === 'stripe' ? 'border-purple-500 bg-purple-50 ring-1 ring-purple-500' : 'border-gray-100 hover:border-gray-300 bg-white'}`}
                                    >
                                        <CreditCard className={`w-6 h-6 mb-2 ${settings.b2c_payment_gateway === 'stripe' ? 'text-purple-600' : 'text-gray-400'}`} />
                                        <p className="font-bold text-gray-800">Stripe</p>
                                        <p className="text-[10px] text-gray-500 mt-1">Le standard mondial. Idéal si vous avez déjà un compte.</p>
                                    </button>
                                </div>
                            </section>

                            {/* CONFIGURATION DYNAMIQUE */}
                            {settings.b2c_payment_gateway !== 'none' && (
                                <section className="animate-in fade-in zoom-in-95">
                                    <div className="flex justify-between items-end border-b border-gray-100 pb-2 mb-4">
                                        <h3 className="font-bold text-gray-700">Clés API {settings.b2c_payment_gateway === 'stripe' ? 'Stripe' : 'Stancer'}</h3>
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-bold text-orange-600">Mode Test (Sandbox)</span>
                                            <Toggle 
                                                checked={settings.b2c_payment_gateway === 'stripe' ? settings.stripe_b2c_test_mode === 'true' : settings.stancer_test_mode === 'true'} 
                                                onChange={(val) => handleChange(settings.b2c_payment_gateway === 'stripe' ? 'stripe_b2c_test_mode' : 'stancer_test_mode', val ? 'true' : 'false')} 
                                            />
                                        </div>
                                    </div>

                                    {/* BLOC STANCER */}
                                    {settings.b2c_payment_gateway === 'stancer' && (
                                        <>
                                            <div className={`space-y-4 p-4 rounded-xl border ${settings.stancer_test_mode === 'true' ? 'bg-gray-50 border-gray-100 opacity-60' : 'bg-green-50 border-green-200'}`}>
                                                <h4 className="text-sm font-bold text-green-800 uppercase tracking-wide">Clés de Production</h4>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Clé Publique</label>
                                                    <input className="w-full font-mono text-sm" placeholder="pkey_prod_..." value={settings.stancer_public_key} onChange={e => handleChange('stancer_public_key', e.target.value)} />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Clé Secrète</label>
                                                    <input type="password" className="w-full font-mono text-sm" placeholder="skey_prod_..." value={settings.stancer_secret_key} onChange={e => handleChange('stancer_secret_key', e.target.value)} />
                                                </div>
                                            </div>
                                            <div className={`space-y-4 p-4 rounded-xl border mt-4 ${settings.stancer_test_mode === 'true' ? 'bg-orange-50 border-orange-200' : 'bg-gray-50 border-gray-100 opacity-60'}`}>
                                                <h4 className="text-sm font-bold text-orange-800 uppercase tracking-wide">Clés de Test</h4>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Clé Publique (Test)</label>
                                                    <input className="w-full font-mono text-sm" placeholder="pkey_test_..." value={settings.stancer_public_key_test} onChange={e => handleChange('stancer_public_key_test', e.target.value)} />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Clé Secrète (Test)</label>
                                                    <input type="password" className="w-full font-mono text-sm" placeholder="skey_test_..." value={settings.stancer_secret_key_test} onChange={e => handleChange('stancer_secret_key_test', e.target.value)} />
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {/* BLOC STRIPE */}
                                    {settings.b2c_payment_gateway === 'stripe' && (
                                        <>
                                            <div className="mb-4 bg-purple-50 border border-purple-200 p-4 rounded-xl">
                                                <p className="text-xs font-bold text-purple-800 uppercase mb-1"> Configuration du Webhook</p>
                                                <p className="text-[10px] text-purple-700 leading-tight">Pour que le logiciel soit prévenu quand un patient a payé, vous devez créer un Endpoint Webhook dans votre tableau de bord Stripe avec cette URL exacte :</p>
                                                <p className="font-mono text-xs bg-white px-2 py-1 rounded mt-2 border border-purple-200 select-all text-purple-900 font-bold">
                                                    {window.location.origin}/api/payments/webhook/stripe-b2c/{workspaceId}
                                                </p>
                                                <p className="text-[10px] text-purple-700 mt-2">Événement à écouter : <strong>checkout.session.completed</strong></p>
                                            </div>

                                            <div className={`space-y-4 p-4 rounded-xl border ${settings.stripe_b2c_test_mode === 'true' ? 'bg-gray-50 border-gray-100 opacity-60' : 'bg-green-50 border-green-200'}`}>
                                                <h4 className="text-sm font-bold text-green-800 uppercase tracking-wide">Clés de Production</h4>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Clé Publique (pk_live_...)</label>
                                                    <input className="w-full font-mono text-sm" placeholder="pk_live_..." value={settings.stripe_b2c_public_key} onChange={e => handleChange('stripe_b2c_public_key', e.target.value)} />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Clé Secrète (sk_live_...)</label>
                                                    <input type="password" className="w-full font-mono text-sm" placeholder="sk_live_..." value={settings.stripe_b2c_secret_key} onChange={e => handleChange('stripe_b2c_secret_key', e.target.value)} />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Clé Webhook (whsec_...)</label>
                                                    <input type="password" className="w-full font-mono text-sm" placeholder="whsec_..." value={settings.stripe_b2c_webhook_secret} onChange={e => handleChange('stripe_b2c_webhook_secret', e.target.value)} />
                                                </div>
                                            </div>
                                            
                                            <div className={`space-y-4 p-4 rounded-xl border mt-4 ${settings.stripe_b2c_test_mode === 'true' ? 'bg-orange-50 border-orange-200' : 'bg-gray-50 border-gray-100 opacity-60'}`}>
                                                <h4 className="text-sm font-bold text-orange-800 uppercase tracking-wide">Clés de Test</h4>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Clé Publique Test (pk_test_...)</label>
                                                    <input className="w-full font-mono text-sm" placeholder="pk_test_..." value={settings.stripe_b2c_public_key_test} onChange={e => handleChange('stripe_b2c_public_key_test', e.target.value)} />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Clé Secrète Test (sk_test_...)</label>
                                                    <input type="password" className="w-full font-mono text-sm" placeholder="sk_test_..." value={settings.stripe_b2c_secret_key_test} onChange={e => handleChange('stripe_b2c_secret_key_test', e.target.value)} />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Clé Webhook Test (whsec_...)</label>
                                                    <input type="password" className="w-full font-mono text-sm" placeholder="whsec_..." value={settings.stripe_b2c_webhook_secret_test} onChange={e => handleChange('stripe_b2c_webhook_secret_test', e.target.value)} />
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {/* GESTION DES ACOMPTES (Commun aux 2 passerelles) */}
                                    <div className="mt-8 border-t border-gray-200 pt-6">
                                        <h3 className="font-bold text-gray-700 pb-2 mb-4">Gestion des Acomptes</h3>
                                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100 mb-4">
                                            <div>
                                                <p className="font-bold text-gray-800">Exiger un acompte en ligne</p>
                                                <p className="text-xs text-gray-500 mt-0.5">Le RDV sera bloqué tant que l'acompte n'est pas réglé.</p>
                                            </div>
                                            <Toggle checked={settings.deposit_enabled === 'true'} onChange={(val) => handleChange('deposit_enabled', val ? 'true' : 'false')} />
                                        </div>
                                        {settings.deposit_enabled === 'true' && (
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-xl border border-gray-100 animate-in fade-in zoom-in-95">
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Montant de l'acompte (€)</label>
                                                    <input type="number" className="w-full font-bold text-sage-700" value={settings.deposit_amount} onChange={e => handleChange('deposit_amount', e.target.value)} />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Appliquer cet acompte pour :</label>
                                                    <select 
                                                        className="w-full" 
                                                        value={settings.deposit_target || 'all'} 
                                                        onChange={e => handleChange('deposit_target', e.target.value)}
                                                    >
                                                        <option value="all">Tous les rendez-vous</option>
                                                        <option value="new_patient">1ère Consultation uniquement</option>
                                                        <option value="video">Téléconsultation (Vidéo) uniquement</option>
                                                    </select>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </section>
                            )}

                        </div>
                    )}


                    {/* ONGLET : NOTIFICATIONS */}
                    {activeTab === 'notifications' && (
                        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4">

                            <section>
                                <h3 className="font-bold text-gray-700 border-b border-gray-100 pb-2 mb-4">Rappels & Délais</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-lg mb-4">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">1er SMS de rappel (Heures avant)</label>
                                        <div className="relative">
                                            <input type="number" className="w-full pr-10" value={settings.reminder_delay_hours} onChange={e => handleChange('reminder_delay_hours', e.target.value)} />
                                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">h</span>
                                        </div>
                                    </div>
                                    <div className="flex flex-col justify-center">
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-2">2ème SMS de rappel (Jour J-1)</label>
                                        <div className="flex items-center gap-3">
                                            <Toggle checked={settings.reminder_24h_enabled === 'true'} onChange={(val) => handleChange('reminder_24h_enabled', val ? 'true' : 'false')} />
                                            <span className="text-sm font-medium text-gray-600">{settings.reminder_24h_enabled === 'true' ? 'Activé' : 'Désactivé'}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-lg">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Limite annulation patient</label>
                                        <div className="relative">
                                            <input type="number" className="w-full pr-10" value={settings.cancellation_delay_hours} onChange={e => handleChange('cancellation_delay_hours', e.target.value)} />
                                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">h</span>
                                        </div>
                                    </div>
                                    <div className="sm:col-span-2 bg-red-50 p-4 rounded-xl border border-red-100">
                                        <label className="block text-xs font-bold text-red-800 uppercase mb-1">Frais d'annulation tardive (€ ou %)</label>
                                        <input type="text" placeholder="Ex: 30€ ou 30%" className="w-full sm:w-1/2 font-bold text-red-700 border-red-200 focus:ring-red-200" value={settings.cancellation_fee_amount} onChange={e => handleChange('cancellation_fee_amount', e.target.value)} />
                                        <p className="text-xs text-red-600 mt-1">Cette mention apparaîtra dans les emails de confirmation envoyés aux patients.</p>
                                    </div>
                                </div>
                            </section>

                            {/* ═══════════════════════════════════════════
                                SECTION EMAIL SMTP
                            ═══════════════════════════════════════════ */}
                            <section>
                                {/* En-tête avec toggle On/Off */}
                                <div className="flex items-center justify-between border-b border-gray-100 pb-3 mb-4">
                                    <h3 className="font-bold text-gray-700 flex items-center gap-2">
                                        <Mail className="w-5 h-5 text-gray-500" /> 
                                        Serveur Email (SMTP)
                                        
                                        {/* NOUVEAU : Bulle d'aide contextuelle */}
                                        <div className="group relative flex items-center ml-1">
                                            <Info className="w-4 h-4 text-blue-400 cursor-help" />
                                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden w-72 p-3 bg-gray-800 text-white text-[10px] rounded-lg shadow-xl group-hover:block z-50 normal-case font-normal leading-relaxed">
                                                <p className="font-bold text-blue-300 mb-1">Comment configurer les e-mails ?</p>
                                                Laissez TOUS les champs vides pour utiliser gratuitement le serveur d'envoi par défaut de BellaBlue (limité).
                                                <br/><br/>
                                                Pour une délivrabilité optimale et utiliser votre propre adresse d'expédition, veuillez renseigner les paramètres de votre boîte mail.
                                                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                                            </div>
                                        </div>
                                    </h3>
                                    <div className="flex items-center gap-3">

                                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                                            settings.smtp_enabled === 'true'
                                                ? 'bg-green-100 text-green-700'
                                                : 'bg-gray-100 text-gray-500'
                                        }`}>
                                            {settings.smtp_enabled === 'true' ? 'Activé' : 'Désactivé'}
                                        </span>
                                        <Toggle
                                            checked={settings.smtp_enabled === 'true'}
                                            onChange={val => handleChange('smtp_enabled', val ? 'true' : 'false')}
                                        />
                                    </div>
                                </div>

                                <div className={`transition-opacity ${settings.smtp_enabled === 'true' ? '' : 'opacity-40 pointer-events-none'}`}>
                                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-4">
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                            <div className="sm:col-span-2">
                                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Serveur (Hôte)</label>
                                                <input className="w-full text-sm font-mono" placeholder="ex: smtp.mondomaine.fr" value={settings.smtp_host || ''} onChange={e => handleChange('smtp_host', e.target.value)} />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Port</label>
                                                <input type="number" className="w-full text-sm font-mono" placeholder="ex: 587" value={settings.smtp_port || ''} onChange={e => handleChange('smtp_port', e.target.value)} />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Utilisateur SMTP</label>
                                                <input className="w-full text-sm" placeholder="ex: contact@mondomaine.fr" value={settings.smtp_user || ''} onChange={e => handleChange('smtp_user', e.target.value)} />
                                            </div>
                                            <div>
                                                <label className="flex items-center gap-1 text-xs font-bold text-gray-500 uppercase mb-1">
                                                    Mot de passe SMTP
                                                    <div className="group relative flex items-center">
                                                        <Info className="w-3.5 h-3.5 text-blue-400 cursor-help" />
                                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden w-64 p-3 bg-gray-800 text-white text-[10px] rounded-lg shadow-xl group-hover:block z-50 normal-case font-normal leading-relaxed">
                                                            <p className="font-bold text-blue-300 mb-1">Gmail / Outlook / Apple</p>
                                                            Si vous utilisez l'un de ces fournisseurs, votre mot de passe classique sera refusé par sécurité. Vous devez générer un <strong>"Mot de passe d'application"</strong> dans les paramètres de sécurité de votre compte email.
                                                            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                                                        </div>
                                                    </div>
                                                </label>
                                                <input type="password" placeholder="********" className="w-full text-sm" value={settings.smtp_password} onChange={e => handleChange('smtp_password', e.target.value)} />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Email expéditeur (From)</label>
                                            <input type="email" placeholder="ex: noreply@mondomaine.fr" className="w-full text-sm" value={settings.smtp_from || ''} onChange={e => handleChange('smtp_from', e.target.value)} />
                                        </div>
                                    </div>

                                    {/* Bouton Test SMTP */}
                                    <div className="mt-4">
                                        <button
                                            onClick={handleTestSmtp}
                                            disabled={smtpTestStatus === 'loading'}
                                            className="btn bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 flex items-center gap-2 text-sm disabled:opacity-50"
                                        >
                                            {smtpTestStatus === 'loading'
                                                ? <Loader className="w-4 h-4 animate-spin" />
                                                : <FlaskConical className="w-4 h-4 text-blue-500" />
                                            }
                                            Tester la connexion SMTP
                                        </button>
                                        <TestStatusBadge status={smtpTestStatus} message={smtpTestMsg} />
                                    </div>
                                </div>

                                {settings.smtp_enabled !== 'true' && (
                                    <p className="text-xs text-gray-400 italic mt-2 flex items-center gap-1">
                                        ⚠️ L'envoi d'emails est désactivé — aucun email ne sera expédié aux patients.
                                    </p>
                                )}
                            </section>

                            {/* ═══════════════════════════════════════════
                                SECTION SMS
                            ═══════════════════════════════════════════ */}
                            <section>
                                {/* En-tête avec toggle On/Off */}
                                <div className="flex items-center justify-between border-b border-gray-100 pb-3 mb-4">
                                    <h3 className="font-bold text-gray-700 flex items-center gap-2">
                                        <Smartphone className="w-5 h-5 text-gray-500" /> Passerelle SMS (Android)
                                    </h3>
                                    <div className="flex items-center gap-3">
                                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                                            settings.sms_enabled === 'true'
                                                ? 'bg-green-100 text-green-700'
                                                : 'bg-gray-100 text-gray-500'
                                        }`}>
                                            {settings.sms_enabled === 'true' ? 'Activé' : 'Désactivé'}
                                        </span>
                                        <Toggle
                                            checked={settings.sms_enabled === 'true'}
                                            onChange={val => handleChange('sms_enabled', val ? 'true' : 'false')}
                                        />
                                    </div>
                                </div>

                                <div className={`transition-opacity ${settings.sms_enabled === 'true' ? '' : 'opacity-40 pointer-events-none'}`}>
                                    {/* Sélecteur mode Cloud / Local */}
                                    <div className="mb-4">
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Mode de connexion</label>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleChange('sms_mode', 'cloud')}
                                                className={`flex-1 py-2 px-4 rounded-xl border-2 font-medium transition-all ${settings.sms_mode === 'cloud' ? 'border-sage-500 bg-sage-50 text-sage-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}
                                            >
                                                ☁️ Cloud (Distant)
                                            </button>
                                            <button
                                                onClick={() => handleChange('sms_mode', 'local')}
                                                className={`flex-1 py-2 px-4 rounded-xl border-2 font-medium transition-all ${settings.sms_mode === 'local' ? 'border-sage-500 bg-sage-50 text-sage-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}
                                            >
                                                📡 Local (WiFi)
                                            </button>
                                        </div>
                                    </div>

                                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-4">
                                        {settings.sms_mode === 'cloud' ? (
                                            <>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">URL Cloud API</label>
                                                    <input placeholder="ex :https://api.sms-gate.app/3rdparty/v1/messages" className="w-full text-sm font-mono" value={settings.sms_cloud_url} onChange={e => handleChange('sms_cloud_url', e.target.value)} />
                                                </div>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Utilisateur Cloud</label>
                                                        <input className="w-full text-sm" value={settings.sms_cloud_user} onChange={e => handleChange('sms_cloud_user', e.target.value)} />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Mot de passe Cloud</label>
                                                        <input type="password" placeholder="********" className="w-full text-sm" value={settings.sms_cloud_password} onChange={e => handleChange('sms_cloud_password', e.target.value)} />
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">URL IP Locale (ex: http://192.168.1.10:8080/message)</label>
                                                    <input className="w-full text-sm font-mono" value={settings.sms_local_url} onChange={e => handleChange('sms_local_url', e.target.value)} />
                                                </div>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Utilisateur App</label>
                                                        <input className="w-full text-sm" value={settings.sms_local_user} onChange={e => handleChange('sms_local_user', e.target.value)} />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Mot de passe App</label>
                                                        <input type="password" placeholder="********" className="w-full text-sm" value={settings.sms_local_password} onChange={e => handleChange('sms_local_password', e.target.value)} />
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>

                                    {/* Bouton Test SMS */}
                                    <div className="mt-4">
                                        <button
                                            onClick={handleTestSms}
                                            disabled={smsTestStatus === 'loading'}
                                            className="btn bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 flex items-center gap-2 text-sm disabled:opacity-50"
                                        >
                                            {smsTestStatus === 'loading'
                                                ? <Loader className="w-4 h-4 animate-spin" />
                                                : <FlaskConical className="w-4 h-4 text-blue-500" />
                                            }
                                            Tester la connexion SMS
                                        </button>
                                        <TestStatusBadge status={smsTestStatus} message={smsTestMsg} />
                                    </div>

                                    {/* Info Box APK */}
                                    <div className="mt-6 bg-blue-50 border border-blue-100 rounded-xl p-4 flex gap-4 items-start">
                                        <div className="p-2 bg-blue-100 rounded-lg text-blue-600 shrink-0">
                                            <Smartphone className="w-6 h-6" />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-blue-900 text-sm">Application SMS Gateway requise (GRATUITE)</h4>
                                            <p className="text-sm text-blue-800 mt-1">
                                                Pour envoyer des SMS, votre téléphone Android doit être configuré avec l'application open-source "SMS Gate".
                                            </p>
                                            <a href="https://sms-gate.app/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-bold text-blue-700 mt-2 hover:underline">
                                                Télécharger l'APK <ExternalLink className="w-3 h-3"/>
                                            </a>
                                        </div>
                                    </div>
                                </div>

                                {settings.sms_enabled !== 'true' && (
                                    <p className="text-xs text-gray-400 italic mt-2 flex items-center gap-1">
                                        ⚠️ L'envoi de SMS est désactivé — aucun SMS ne sera expédié aux patients.
                                    </p>
                                )}
                            </section>
                        </div>
                    )}

                    {/* ONGLET : SITE VITRINE */}
                    {activeTab === 'website' && landingConfig && (
                        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4">

                            <section>
                                <h3 className="font-bold text-gray-700 border-b border-gray-100 pb-2 mb-4">Photo de Profil</h3>
                                <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 max-w-md flex items-center gap-6">
                                    <div className="w-24 h-24 rounded-full bg-white border border-gray-200 flex items-center justify-center overflow-hidden shrink-0 shadow-sm">
                                        {settings.avatar_url ? (
                                            <img src={settings.avatar_url} alt="Avatar" className="w-full h-full object-cover" />
                                        ) : (
                                            <UserRound className="w-8 h-8 text-gray-400" />
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-sm font-bold text-gray-800 mb-1">Votre portrait</p>
                                        <p className="text-xs text-gray-500 mb-3">Sera affiché sur la page de prise de RDV.</p>
                                        <label className="btn bg-white border border-gray-300 text-gray-700 hover:bg-gray-100 cursor-pointer text-xs py-2 px-4 inline-flex items-center gap-2">
                                            <Upload className="w-4 h-4"/>
                                            Changer la photo
                                            <input type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} />
                                        </label>
                                    </div>
                                </div>
                            </section>
                            
                            <section>
                                <h3 className="font-bold text-gray-700 border-b border-gray-100 pb-2 mb-4">Identité Visuelle (Couleurs)</h3>
                                <div className="grid grid-cols-2 gap-6 max-w-md">
                                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Textes Principaux</label>
                                        <div className="flex gap-3 items-center">
                                            <input type="color" value={landingConfig.theme?.text_color || '#544541'} onChange={e => handleLandingChange('theme', 'text_color', e.target.value)} className="w-12 h-12 p-1 border border-gray-300 rounded cursor-pointer" />
                                            <span className="text-sm font-mono text-gray-500 bg-white px-2 py-1 rounded border border-gray-200">{landingConfig.theme?.text_color || '#544541'}</span>
                                        </div>
                                    </div>
                                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Fonds Secondaires</label>
                                        <div className="flex gap-3 items-center">
                                            <input type="color" value={landingConfig.theme?.bg_color || '#ECEADA'} onChange={e => handleLandingChange('theme', 'bg_color', e.target.value)} className="w-12 h-12 p-1 border border-gray-300 rounded cursor-pointer" />
                                            <span className="text-sm font-mono text-gray-500 bg-white px-2 py-1 rounded border border-gray-200">{landingConfig.theme?.bg_color || '#ECEADA'}</span>
                                        </div>
                                    </div>
                                </div>
                            </section>

                            <section>
                                <h3 className="font-bold text-gray-700 border-b border-gray-100 pb-2 mb-4">Affichage public</h3>
                                <div className="space-y-4 max-w-md">
                                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                                        <div>
                                            <p className="font-bold text-gray-800">Afficher les Tarifs</p>
                                            <p className="text-xs text-gray-500">Afficher la grille tarifaire sur votre vitrine générique.</p>
                                        </div>
                                        <Toggle checked={settings.show_tariffs_publicly === 'true'} onChange={(val) => handleChange('show_tariffs_publicly', val ? 'true' : 'false')} />
                                    </div>
                                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                                        <div>
                                            <p className="font-bold text-gray-800">Afficher le Téléphone</p>
                                            <p className="text-xs text-gray-500">Rendre visible sur votre vitrine générique.</p>
                                        </div>
                                        <Toggle checked={settings.show_phone_publicly === 'true'} onChange={(val) => handleChange('show_phone_publicly', val ? 'true' : 'false')} />
                                    </div>
                                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                                        <div>
                                            <p className="font-bold text-gray-800">Afficher l'Email</p>
                                            <p className="text-xs text-gray-500">Rendre visible sur votre vitrine générique.</p>
                                        </div>
                                        <Toggle checked={settings.show_email_publicly === 'true'} onChange={(val) => handleChange('show_email_publicly', val ? 'true' : 'false')} />
                                    </div>
                                </div>
                            </section>

                            <section>
                                <h3 className="font-bold text-gray-700 border-b border-gray-100 pb-2 mb-4">Partage & Impression (QR Code)</h3>
                                <div className="bg-sage-50 p-6 rounded-xl border border-sage-200 max-w-2xl flex flex-col md:flex-row items-center gap-8">
                                    {/* Le QR Code visuel */}
                                    <div className="flex flex-col items-center gap-2 shrink-0">
                                        <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
                                            <QRCodeCanvas 
                                                id="qr-canvas"
                                                value={qrType === 'url' ? `${actualUrl}/booking` : vCardData}
                                                size={160}
                                                level={"H"} 
                                                fgColor="#111827" 
                                                imageSettings={qrWithLogo && settings.avatar_url ? {
                                                    src: settings.avatar_url,
                                                    x: undefined,
                                                    y: undefined,
                                                    height: 35,
                                                    width: 35,
                                                    excavate: true, // Creuse le QR code autour du logo pour la lisibilité
                                                } : undefined}
                                            />
                                        </div>
                                        <p className="text-[9px] text-gray-500 text-center max-w-[160px] leading-tight">
                                            Généré en contraste maximal pour garantir la lecture à l'impression.
                                        </p>
                                    </div>

                                    {/* Les options */}
                                    <div className="flex-1 space-y-5 w-full">
                                        {/* Option Logo */}
                                        {settings.avatar_url && (
                                            <label className="flex items-center gap-2 cursor-pointer bg-white p-2 rounded-lg border border-sage-100 w-fit shadow-sm">
                                                <input 
                                                    type="checkbox" 
                                                    checked={qrWithLogo} 
                                                    onChange={(e) => setQrWithLogo(e.target.checked)}
                                                    className="w-4 h-4 text-sage-600 rounded focus:ring-sage-500"
                                                />
                                                <span className="text-xs font-bold text-sage-700">Inclure ma photo au centre</span>
                                            </label>
                                        )}
                                        <div>
                                            <p className="text-sm font-bold text-sage-800 mb-3">Que doit faire le téléphone du patient lors du scan ?</p>
                                            <div className="flex bg-white rounded-lg p-1 border border-sage-200">
                                                <button 
                                                    onClick={() => setQrType('url')}
                                                    className={`flex-1 px-3 py-2 text-xs font-bold rounded-md transition-all ${
                                                        qrType === 'url' ? 'bg-sage-600 text-white shadow-sm' : 'text-sage-600 hover:bg-sage-50'
                                                    }`}
                                                >
                                                    Ouvrir la prise de RDV
                                                </button>
                                                <button 
                                                    onClick={() => setQrType('vcard')}
                                                    className={`flex-1 px-3 py-2 text-xs font-bold rounded-md transition-all ${
                                                        qrType === 'vcard' ? 'bg-sage-600 text-white shadow-sm' : 'text-sage-600 hover:bg-sage-50'
                                                    }`}
                                                >
                                                    Créer une Fiche Contact
                                                </button>
                                            </div>
                                            <p className="text-[10px] text-gray-500 mt-2">
                                                {qrType === 'url' 
                                                    ? `Ouvrira : ${actualUrl}/booking`
                                                    : "Le smartphone proposera d'enregistrer votre Nom, Téléphone, Email et Adresse dans le carnet d'adresses du patient."}
                                            </p>
                                        </div>

                                        <button 
                                            onClick={downloadQRCode}
                                            className="w-full btn bg-white border border-sage-300 text-sage-700 hover:bg-sage-100 flex items-center justify-center gap-2 py-2.5 shadow-sm"
                                        >
                                            <Download className="w-4 h-4"/> Télécharger l'image (PNG)
                                        </button>
                                    </div>
                                </div>
                            </section>

                            <section>
                                <h3 className="font-bold text-gray-700 border-b border-gray-100 pb-2 mb-4">📱 Options du Portail Patient (Par défaut)</h3>
                                <div className="space-y-4 max-w-md">
                                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                                        <div>
                                            <p className="font-bold text-gray-800">Suivi du Poids</p>
                                            <p className="text-xs text-gray-500">Activer la courbe et l'enregistrement de poids pour les nouveaux espaces patients.</p>
                                        </div>
                                        <Toggle
                                            checked={settings.portal_show_weight !== 'false'}
                                            onChange={(val) => handleChange('portal_show_weight', val ? 'true' : 'false')}
                                        />
                                    </div>

                                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                                        <div>
                                            <p className="font-bold text-gray-800">Carnet Alimentaire & Plan</p>
                                            <p className="text-xs text-gray-500">Activer le journal de repas et l'accès au programme diététique par défaut.</p>
                                        </div>
                                        <Toggle
                                            checked={settings.portal_show_meals !== 'false'}
                                            onChange={(val) => handleChange('portal_show_meals', val ? 'true' : 'false')}
                                        />
                                    </div>

                                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                                        <div>
                                            <p className="font-bold text-gray-800">Carnet d'Activité Physique</p>
                                            <p className="text-xs text-gray-500">Activer le suivi et l'historique d'exercices physiques par défaut.</p>
                                        </div>
                                        <Toggle
                                            checked={settings.portal_show_activity !== 'false'}
                                            onChange={(val) => handleChange('portal_show_activity', val ? 'true' : 'false')}
                                        />
                                    </div>
                                </div>
                            </section>


                        </div>
                    )}

                    {/* ONGLET : STOCKAGE ET ABONNEMENT */}
                    {activeTab === 'storage' && storageInfo && (
                        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 max-w-lg">
                            
                            <section>
                                <h3 className="font-bold text-gray-700 border-b border-gray-100 pb-2 mb-4">Votre Abonnement BellaBlue</h3>
                                <div className="bg-sage-50 border border-sage-200 rounded-xl p-6">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="w-10 h-10 bg-sage-200 text-sage-700 rounded-full flex items-center justify-center shadow-inner">
                                            <Shield className="w-5 h-5"/>
                                        </div>
                                        <div>
                                            <p className="font-bold text-gray-800 text-lg">Plan Premium</p>
                                            <p className="text-xs text-sage-600 font-bold uppercase tracking-widest">TOUT INCLUS</p>
                                        </div>
                                    </div>
                                    
                                    <p className="text-sm text-gray-600 mb-6 leading-relaxed">Profitez de toutes les fonctionnalités sans limite : agenda intelligent, rappels SMS, portail patient sécurisé et tant d'autres options !.</p>
                                    
                                    {/* CAS 1 : ACTIF & FACTURÉ (Standard) */}
                                    {storageInfo.subscription_status === 'active' && storageInfo.has_billing && (
                                        <div className="space-y-4 animate-in zoom-in-95">
                                            {/* ASTUCE : Si la date de fin est dans plus de 45 jours, c'est un abonnement annuel ! */}
                                            {(() => {
                                                const daysUntilRenewal = storageInfo.subscription_end_date 
                                                    ? (new Date(storageInfo.subscription_end_date) - new Date()) / (1000 * 60 * 60 * 24) 
                                                    : 0;
                                                const isMonthly = daysUntilRenewal <= 45;
                                                
                                                return (
                                                    <>
                                            <div className="bg-white p-5 rounded-xl border border-sage-100 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-sm">
                                                <div>
                                                    <p className="text-green-600 font-bold flex items-center gap-2">
                                                        <CheckCircle className="w-5 h-5"/> Abonnement actif
                                                    </p>
                                                    {storageInfo.subscription_end_date && (
                                                        <p className="text-xs font-bold text-sage-600 mt-1.5 tracking-wide">
                                                            Renouvellement le : {new Date(storageInfo.subscription_end_date).toLocaleDateString('fr-FR')}
                                                        </p>
                                                    )}
                                                    <p className="text-[10px] text-gray-500 mt-1">Vos paiements et factures sont gérés automatiquement, par Stripe.</p>
                                                </div>
                                                <button 
                                                    onClick={async () => {
                                                        const toastId = toast.loading("Ouverture de votre portail sécurisé...");
                                                        try {
                                                            const res = await api.post('/api/workspaces/customer-portal')
                                                            window.location.href = res.data.url
                                                        } catch(e) { toast.error("Erreur de connexion", { id: toastId }) }
                                                    }}
                                                    className="btn bg-sage-600 text-white hover:bg-sage-700 shadow-md text-sm whitespace-nowrap"
                                                >
                                                    <CreditCard className="w-4 h-4 inline-block mr-2"/>
                                                    Gérer mon abonnement
                                                </button>
                                            </div>

                                            {/* BANNIERE UPSELL ANNUEL (Seulement si mensuel) */}
                                            {isMonthly && (
                                                <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-4 flex items-center justify-between shadow-sm">
                                                    <div>
                                                        <p className="text-sm font-bold text-orange-800">Passez à l'abonnement annuel !</p>
                                                        <p className="text-xs text-orange-700 mt-0.5">Économisez 2 mois offerts en modifiant votre plan depuis votre portail de gestion. Allez vite sur l'option "Gérer mon abonnement" !</p>
                                                    </div>
                                                    <Gift className="w-8 h-8 text-orange-400 hidden sm:block shrink-0 ml-4" />
                                                </div>
                                            )}
                                                    </>
                                                )
                                            })()}
                                        </div>
                                    )}


                                    {/* CAS 2 : ACTIF & NON FACTURÉ (VIP / Fondateur) */}
                                    {storageInfo.subscription_status === 'active' && !storageInfo.has_billing && (
                                        <div className="bg-gradient-to-r from-amber-100 to-yellow-50 p-5 rounded-xl border border-yellow-200 flex flex-col sm:flex-row items-center gap-4 shadow-sm animate-in zoom-in-95">
                                            <div className="p-2 bg-yellow-200 rounded-full text-yellow-700"><Shield className="w-6 h-6"/></div>
                                            <div>
                                                <p className="text-yellow-800 font-bold text-lg">Compte Fondateur VIP</p>
                                                <p className="text-sm text-yellow-700 mt-1">💝Vous bénéficiez d'un accès gratuit et illimité à vie. Merci pour votre confiance historique ! 😍🎉</p>
                                            </div>
                                        </div>
                                    )}

                                    {/* CAS 3 : PÉRIODE D'ESSAI OU EXPIRÉ */}
                                    {storageInfo.subscription_status !== 'active' && (
                                        <>
                                            {storageInfo.subscription_status === 'trialing' && (
                                                <div className="mb-6 bg-blue-50 border border-blue-200 p-4 rounded-xl">
                                                    <p className="text-sm font-bold text-blue-800">⏳ Période d'essai en cours</p>
                                                    <p className="text-xs text-blue-600 mt-1">
                                                        Il vous reste <strong>{Math.max(0, 14 - Math.floor((new Date() - new Date(storageInfo.created_at)) / (1000 * 60 * 60 * 24)))} jours</strong> d'essai gratuit. 
                                                        Sécurisez votre utilisation dès maintenant !
                                                    </p>
                                                </div>
                                            )}
                                            <div className="grid grid-cols-2 gap-4">
                                                <button 
                                                    onClick={async () => {
                                                        const toastId = toast.loading("Redirection vers la passerelle sécurisée...");
                                                        try {
                                                            const res = await api.post('/api/workspaces/create-checkout-session', { plan_type: 'monthly', return_url: window.location.origin })
                                                            window.location.href = res.data.url
                                                        } catch(e) { toast.error("Erreur de connexion", { id: toastId }) }
                                                    }}
                                                    className="btn bg-white border-2 border-sage-300 text-sage-700 hover:bg-sage-100 hover:border-sage-400 flex flex-col items-center py-4 transition-all"
                                                >
                                                    <span className="font-black text-2xl">{subPlans.monthly}€</span>
                                                    <span className="text-xs uppercase font-bold mt-1">/ mois</span>
                                                </button>
                                                
                                                <button 
                                                    onClick={async () => {
                                                        const toastId = toast.loading("Redirection vers la passerelle sécurisée...");
                                                        try {
                                                            const res = await api.post('/api/workspaces/create-checkout-session', { plan_type: 'annual', return_url: window.location.origin })
                                                            window.location.href = res.data.url
                                                        } catch(e) { toast.error("Erreur de connexion", { id: toastId }) }
                                                    }}
                                                    className="btn bg-sage-600 text-white hover:bg-sage-700 flex flex-col items-center py-4 shadow-lg transform transition-all hover:-translate-y-1 relative border-2 border-sage-600"
                                                >
                                                    <div className="absolute -top-3 right-[-10px] bg-orange-500 text-white text-[10px] font-black px-2 py-1 rounded-full shadow-sm rotate-12">
                                                        2 MOIS OFFERTS !
                                                    </div>
                                                    <span className="font-black text-2xl">{subPlans.annual}€</span>
                                                    <span className="text-xs uppercase font-bold opacity-80 mt-1">/ an</span>
                                                </button>
                                            </div>
                                            <p className="text-[10px] text-gray-400 text-center mt-4 flex items-center justify-center gap-1">
                                                <Shield className="w-3 h-3" /> Paiement sécurisé par Stripe. Sans engagement.
                                            </p>
                                        </>
                                    )}
                                </div>
                            </section>

                            <section>
                                <h3 className="font-bold text-gray-700 border-b border-gray-100 pb-2 mb-4">Espace de stockage (Dossiers Médicaux)</h3>
                                <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                                    <div className="flex justify-between items-end mb-2">
                                        <div>
                                            <p className="text-sm font-bold text-gray-800">Utilisation du disque</p>
                                            <p className="text-xs text-gray-500">Documents, factures, et photos patients.</p>
                                        </div>
                                        <div className="text-right">
                                            <span className="text-2xl font-black text-sage-600">{storageInfo.usage_mb} Mo</span>
                                            <span className="text-sm text-gray-500 font-medium"> / {storageInfo.quota_mb} Mo</span>
                                        </div>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-3 mb-2 overflow-hidden shadow-inner">
                                        <div
                                            className={`h-3 rounded-full transition-all duration-500 ${storageInfo.usage_percent > 90 ? 'bg-red-500' : storageInfo.usage_percent > 75 ? 'bg-orange-500' : 'bg-sage-500'}`}
                                            style={{ width: `${Math.min(storageInfo.usage_percent, 100)}%` }}
                                        ></div>
                                    </div>
                                    <p className="text-xs text-gray-400 text-right mb-4">{storageInfo.usage_percent}% utilisé</p>
                                    
                                    <div className="pt-4 border-t border-gray-100 flex items-center justify-between">
                                        <div>
                                            <p className="text-sm font-bold text-gray-800">Besoin de plus d'espace ?</p>
                                            <p className="text-xs text-gray-500">Ajoutez 5 Go supplémentaires à votre compte à vie.</p>
                                        </div>
                                        <button 
                                            onClick={async () => {
                                                const toastId = toast.loading("Redirection sécurisée...")
                                                try {
                                                    const res = await api.post('/api/workspaces/create-checkout-quota')
                                                    window.location.href = res.data.url
                                                } catch(e) { toast.error("Bientôt disponible !", { id: toastId }) }
                                            }}
                                            className="btn bg-gray-800 text-white hover:bg-black text-sm px-4 py-2"
                                        >
                                            Acheter +5 Go ({subPlans.quota}€)
                                        </button>
                                    </div>
                                </div>
                            </section>
                        </div>
                    )}
                    
                    {/* ONGLET : SÉCURITÉ */}
                    {activeTab === 'security' && (
                        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 max-w-lg">
                            <section>
                                <h3 className="font-bold text-gray-700 border-b border-gray-100 pb-2 mb-4">Identifiant de connexion</h3>
                                <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Email administrateur</label>
                                    <input type="email" value={adminEmail} disabled className="w-full bg-gray-100 text-gray-500 cursor-not-allowed" />
                                    <p className="text-xs text-gray-400 mt-2">L'adresse email de connexion ne peut être modifiée par mesure de sécurité.</p>
                                </div>
                            </section>
                            <section>
                                <h3 className="font-bold text-gray-700 border-b border-gray-100 pb-2 mb-4">Changer de mot de passe</h3>
                                <form onSubmit={handlePasswordChange} className="space-y-4 bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Mot de passe actuel</label>
                                        <input type="password" required value={pwdData.current} onChange={e => setPwdData({...pwdData, current: e.target.value})} className="w-full" />
                                    </div>
                                    <div className="pt-2 border-t border-gray-50">
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Nouveau mot de passe</label>
                                        <input type="password" required minLength="6" value={pwdData.new} onChange={e => setPwdData({...pwdData, new: e.target.value})} className="w-full mb-3" />
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Confirmer le nouveau mot de passe</label>
                                        <input type="password" required minLength="6" value={pwdData.confirm} onChange={e => setPwdData({...pwdData, confirm: e.target.value})} className="w-full" />
                                    </div>
                                    <div className="pt-4 text-right">
                                        <button
                                            type="submit"
                                            disabled={changingPwd || !pwdData.current || !pwdData.new}
                                            className="btn bg-gray-800 text-white hover:bg-black w-full sm:w-auto"
                                        >
                                            {changingPwd ? <div className="spinner w-5 h-5 border-white"/> : "Mettre à jour le mot de passe"}
                                        </button>
                                    </div>
                                </form>
                            </section>

                            <section>
                                <h3 className="font-bold text-gray-700 border-b border-gray-100 pb-2 mb-4">Export des données (RGPD)</h3>
                                <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
                                    <div>
                                        <p className="font-bold text-gray-800">Droit à la portabilité</p>
                                        <p className="text-xs text-gray-500 mt-1">Téléchargez l'intégralité de vos dossiers patients, notes de consultations, et historique de rendez-vous au format CSV standard.</p>
                                    </div>
                                    <button 
                                        onClick={async () => {
                                            const toastId = toast.loading("Préparation de vos archives (ZIP)...")
                                            try {
                                                const response = await api.get('/api/admin/export-rgpd', { responseType: 'blob' })
                                                const url = window.URL.createObjectURL(new Blob([response.data]))
                                                const link = document.createElement('a')
                                                link.href = url
                                                link.setAttribute('download', `Export_BellaBlue.zip`)
                                                document.body.appendChild(link)
                                                link.click()
                                                link.remove()
                                                toast.success("Archive téléchargée !", { id: toastId })
                                            } catch (error) { toast.error("Erreur d'export", { id: toastId }) }
                                        }}
                                        className="btn bg-white border border-gray-300 text-gray-700 hover:bg-gray-100 flex items-center gap-2 whitespace-nowrap shadow-sm"
                                    >
                                        <Download className="w-4 h-4"/> Exporter (ZIP)
                                    </button>
                                </div>
                            </section>
                        </div>
                    )}

                </div>

                {/* FOOTER ACTIONS */}
                {activeTab !== 'security' && (
                    <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end">
                        <button onClick={handleSave} disabled={saving} className="btn btn-primary flex items-center justify-center gap-2 w-full sm:w-auto px-8">
                            {saving ? <div className="spinner w-5 h-5 border-white"/> : <><Save className="w-5 h-5"/> Enregistrer les modifications</>}
                        </button>
                    </div>
                )}
            </div>
        </div>
    )
}

